import {Injectable, inject} from '@angular/core';
import {MatSnackBar, MatSnackBarRef} from '@angular/material/snack-bar';
import {Observable, Subscription} from 'rxjs';
import {shareReplay} from 'rxjs/operators';
import {ProgressIndicatorComponent} from '../components/progress-indicator/progress-indicator.component';
import {NotificationIndicatorComponent} from '../components/notification-indicator/notification-indicator.component';
import {ToastType} from '../models/toast-type';

@Injectable({
  providedIn: 'root',
})
export class SnackbarService {
  snackBar = inject(MatSnackBar);

  private inProgress = false;
  private myMap = new Map<string, boolean>();
  private messageShowDuration = 4000;
  private warningShowDuration = 5000;

  isInProgress(taskKey = ''): boolean {
    if (taskKey && taskKey.length > 0) {
      if (this.myMap.has(taskKey)) {
        return this.myMap.get(taskKey) as boolean;
      }
    } else {
      return this.inProgress;
    }
    return this.inProgress;
  }

  /*These 4 states are used for typical back end calls and feedback from observables, isInProgress can be queried to check if the state is still in progress*/
  /*Multiple tasks can run at the same time and can be tracked via the taskKey*/

  /*-----------------------------------------------------------------------------------*/
  notifyInProgress(message: string, taskKey = '') {
    if (taskKey && taskKey.length > 0) {
      this.myMap.set(taskKey, true);
    } else {
      this.inProgress = true;
    }

    this.snackBar.openFromComponent(ProgressIndicatorComponent, {
      data: message,
      panelClass: ['snackbar-progress'],
    });
  }

  notifyInProgressWithoutIndicator(message: string, taskKey = '') {
    if (taskKey && taskKey.length > 0) {
      this.myMap.set(taskKey, true);
    } else {
      this.inProgress = true;
    }

    this.snackBar.openFromComponent(NotificationIndicatorComponent, {
      data: {message: message, notificationType: ToastType.INFO},
      panelClass: ['snackbar-info'],
    });
  }

  notifySuccess(message: string, taskKey = '') {
    if (taskKey && taskKey.length > 0) {
      if (this.myMap.has(taskKey)) {
        this.myMap.set(taskKey, false);
      }
    } else {
      this.inProgress = false;
    }

    this.snackBar.openFromComponent(NotificationIndicatorComponent, {
      data: {message: message, notificationType: ToastType.SUCCESS},
      duration: this.messageShowDuration,
      panelClass: ['snackbar-positive'],
    });
  }

  notifyError(message: string, trace = '', taskKey = '') {
    if (taskKey && taskKey.length > 0) {
      if (this.myMap.has(taskKey)) {
        this.myMap.set(taskKey, false);
      }
    } else {
      this.inProgress = false;
    }

    this.snackBar.openFromComponent(NotificationIndicatorComponent, {
      data: {
        message: message,
        notificationType: ToastType.ERROR,
        trace: trace,
      },
      panelClass: ['snackbar-error'],
    });
  }

  notifyWithAction(message: string, action: string, trace = '', taskKey = ''): MatSnackBarRef<NotificationIndicatorComponent> {
    if (taskKey && taskKey.length > 0) {
      if (this.myMap.has(taskKey)) {
        this.myMap.set(taskKey, false);
      }
    } else {
      this.inProgress = false;
    }

    return this.snackBar.openFromComponent(NotificationIndicatorComponent, {
      data: {
        message: message,
        notificationType: ToastType.ACTION,
        action: action,
        trace: trace,
      },
      panelClass: ['snackbar-positive'],
    });
  }

  dismissInProgress(taskKey = '') {
    if (taskKey && taskKey.length > 0) {
      if (this.myMap.has(taskKey)) {
        this.myMap.set(taskKey, false);
        this.inProgress = false;
        this.snackBar.dismiss();
      }
    } else {
      this.inProgress = false;
      this.snackBar.dismiss();
    }
  }

  notifyOn<T>(startMessage: string, successMessage: string, errorMessage: string, observable: Observable<T>) {
    this.notifyInProgress(startMessage);

    const watchOn: Observable<T> = observable.pipe(shareReplay());
    const subscription: Subscription = watchOn.subscribe({
      next: () => {
        this.notifySuccess(successMessage);
      },
      error: () => {
        this.notifyError(errorMessage);
      },
      complete: () => {
        // console.log('Notification unsubscribing');
        subscription.unsubscribe();
      },
    });

    return observable;
  }

  notifyWatch<T>(itemMessage: string, observable: Observable<T>) {
    this.notifyInProgress('Loading ' + itemMessage + '...');

    const watchOn: Observable<T> = observable.pipe(shareReplay());
    const subscription: Subscription = watchOn.subscribe({
      next: () => {
        this.notifySuccess(itemMessage + ' loaded');
      },
      error: () => {
        this.notifyError('Unable to load ' + itemMessage);
      },
      complete: () => {
        subscription.unsubscribe();
      },
    });

    return observable;
  }

  /*-----------------------------------------------------------------------------------*/

  notifyInfo(message: string, duration = 0) {
    this.snackBar.openFromComponent(NotificationIndicatorComponent, {
      data: {message: message, notificationType: ToastType.INFO},
      duration: duration > 0 ? duration : this.messageShowDuration,
      panelClass: ['snackbar-info'],
    });
  }

  notifyWarning(message: string) {
    this.snackBar.openFromComponent(NotificationIndicatorComponent, {
      data: {message: message, notificationType: ToastType.WARNING},
      duration: this.warningShowDuration,
      panelClass: ['snackbar-caution'],
    });
  }
}
